import { AxiosError } from 'axios';

import { ValidationError } from '../errors';
import { controllerHelpers, type Controller, type Listeners } from '../helpers';
import {
  type SingleBillInfoFormManager,
  type SingleBillPaymentMethodsManager,
  type UpdateSingleBillManager,
  type AlertManager,
  singleBillInfoFormEventBus,
} from '../managers';
import { updateSingleBillEventBusEventBus } from '../managers/UpdateSingleBillManager/UpdateSingleBillEventBus';

export class UpdateSingleBillController implements Controller {
  public listeners: Listeners;

  constructor(
    private paymentMethodsManger: SingleBillPaymentMethodsManager,
    private singleBillInfoForm: SingleBillInfoFormManager,
    private updateSingleBillManager: UpdateSingleBillManager,
    private alertManager: AlertManager
  ) {
    const eventConnections = [
      updateSingleBillEventBusEventBus.bindTo('requestSingleBillUpdate', this.updateSingleBill),
      updateSingleBillEventBusEventBus.bindTo('requestSingleBillCreate', this.createSingleBill),
      singleBillInfoFormEventBus.bindTo('formDirtyStateChanged', this.onDirtyStateChanged),
    ];

    this.listeners = controllerHelpers.createListeners(eventConnections);
  }

  private updateSingleBill = async () => {
    await this.submitSingleBill('update');
  };

  private createSingleBill = async () => {
    await this.submitSingleBill('create');
  };

  private submitSingleBill = async (operation: 'update' | 'create') => {
    const [paymentMethods, singleBillInfoForm, updateSingleBill, alertApi] = await Promise.all([
      this.paymentMethodsManger.getApiWhenReady(),
      this.singleBillInfoForm.getApiWhenReady(),
      this.updateSingleBillManager.getApiWhenReady(),
      this.alertManager.getApiWhenReady(),
    ]);

    if (singleBillInfoForm.isFormDirty() || paymentMethods.isNewPaymentMethod()) {
      // reset error and set loading to true
      updateSingleBill.reportBeginLoading();

      // set widgets to loading state
      singleBillInfoForm.setIsLoading(true);
      paymentMethods.setTabsLoading(true);

      // remove existing alerts
      alertApi?.removeAllAlerts();

      try {
        const payload: any = await singleBillInfoForm.getValuesForApiRequest();

        payload.payment_method = await paymentMethods.getPayloadForCheckoutApi({
          postalCode: payload.customer.zipcode,
          countryCode: payload.customer.country,
          fullName: `${payload.customer.firstname} ${payload.customer.lastname}`,
          email: payload.customer.email,
          phone: payload.customer.phone,
        });

        if (operation === 'update') {
          await updateSingleBill.submitUpdateSingleBill(payload);
        } else {
          await updateSingleBill.submitCreateSingleBill(payload);
        }

        setTimeout(() => this.resetState(), 10);

        updateSingleBill.reportSuccess();

        const { message, header } = updateSingleBill.getSuccessMessage();

        alertApi?.addAlert({
          variant: 'success',
          header,
          message,
          id: message,
        });
      } catch (error) {
        // Ignore validation errors here, since they are reported directly on the form fields.
        if (error instanceof ValidationError) {
          updateSingleBill.reportEndLoading();
        } else if ((error instanceof AxiosError || error instanceof Error) && error.message) {
          updateSingleBill.reportError(error);

          // try to display a more user-friendly error message, fallback to the error message
          const message =
            error instanceof AxiosError && typeof error.response?.data === 'string'
              ? error.response?.data
              : error.message;

          alertApi?.addAlert({
            message,
            id: message,
          });
        } else {
          const newError = new Error(`Unknown error: ${String(error)}`);

          updateSingleBill.reportError(newError);

          alertApi?.addAlert({
            message: newError.message,
            id: newError.message,
          });
        }
      } finally {
        singleBillInfoForm.setIsLoading(false);
        paymentMethods.setTabsLoading(false);
        updateSingleBill.reportEndLoading();
      }
    }
  };

  private onDirtyStateChanged = async (dirtyState: boolean) => {
    const api = await this.updateSingleBillManager.getApiWhenReady();

    api.reportFormDirtyState(dirtyState);
  };

  private resetState = async () => {
    const [paymentMethods, singleBillInfoForm, updateSingleBill] = await Promise.all([
      this.paymentMethodsManger.getApiWhenReady(),
      this.singleBillInfoForm.getApiWhenReady(),
      this.updateSingleBillManager.getApiWhenReady(),
    ]);

    paymentMethods.resetState();
    singleBillInfoForm.resetState();
    updateSingleBill.reportFormDirtyState(false);
    updateSingleBill.reportPaymentCanBeUpdated(false);
  };
}
